"use client";
import { ShoppingCart, ScanIcon } from "lucide-react";
import { useState } from "react";



const OPTIONS = [
  { month: "one month", gb: 5, price: 7 },
  { month: "one month", gb: 10, price: 14 },
  { month: "one month", gb: 20, price: 20 },
  { month: "three month", gb: 50, price: 40 },
];

type CartItem = {
  month: string;
  gb: number;
  price: number;
  quantity: number;
};

export default function Pricing() {
  const [cart, setCart] = useState<CartItem[]>([]);

  function handleAddToCart(option: (typeof OPTIONS)[0]) {
    setCart((prev) => {
      const exists = prev.find(
        (item) => item.month === option.month && item.gb === option.gb,
      );
      if (exists) {
        return prev.map((item) =>
          item.month === option.month && item.gb === option.gb
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prev, { ...option, quantity: 1 }];
    });
  }

  function handleRemoveFromCart(month: string, gb: number) {
    setCart((prev) =>
      prev
        .map((item) =>
          item.month === month && item.gb === gb
            ? { ...item, quantity: item.quantity - 1 }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  }

  const total = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <section
      id="usa-esim-price-chart"
      className="py-12 lg:py-[4.5rem] px-5 md:px-8 lg:px-0 w-full"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span
              className="w-6 h-6 rounded-full overflow-hidden shadow flex flex-col"
              aria-label="Ukraine flag"
            >
              <span className="block flex-1 w-full bg-[#0057b7]" />
              <span className="block flex-1 w-full bg-[#ffd700]" />
            </span>
            <span className="text-primary uppercase text-sm font-bold tracking-wide lg:text-[1.55rem]">
              Ukraine (5G)
            </span>
          </div>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-foreground">
            <span className="dark:text-[#d100ff] font-medium"></span>
          </h2>
        </div>
        <div className="flex flex-col lg:flex-row gap-8 items-start w-full">
          <div className="w-full flex-1">
            <div className="overflow-x-auto">
              <table className="min-w-full bg-card rounded-lg shadow">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground bg-muted">
                      <span className="lg:text-[1.4rem]">Month</span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground bg-muted">
                      <span className="lg:text-[1.4rem]">Data (GB)</span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground bg-muted">
                      <span className="lg:text-[1.4rem]">Price (USD)</span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground bg-muted"></th>
                  </tr>
                </thead>
                <tbody>
                  {OPTIONS.map((option) => (
                    <tr
                      key={option.month + "-" + option.gb + "-" + option.price}
                      className="border-b border-muted"
                    >
                      <td className="px-4 py-3 text-card-foreground">
                        <span>{option.month}</span>
                      </td>
                      <td className="px-4 py-3 text-card-foreground">
                        <span>{option.gb}</span>
                      </td>
                      <td className="px-4 py-3 text-card-foreground">
                        $<span>{option.price}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-5 py-2 rounded transition-colors"
                          type="button"
                          onClick={() => handleAddToCart(option)}
                        >
                          Purchase
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="w-full lg:w-80 bg-card rounded-lg shadow px-6 py-6">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-lg text-card-foreground">
                Shopping Cart
              </h3>
            </div>
            {cart.length === 0 ? (
              <div className="text-muted-foreground text-sm py-6 text-center">
                Your cart is empty
              </div>
            ) : (
              <ul className="space-y-4 mb-4">
                {cart.map((item) => (
                  <li
                    key={item.month + "-" + item.gb + "-" + item.price}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <span className="font-medium text-card-foreground">
                        <span>{item.month}</span>
                      </span>
                      <span className="text-muted-foreground text-xs ml-2">
                        (<span>{item.gb}</span>GB)
                      </span>
                      <span className="ml-2 text-card-foreground">
                        x<span>{item.quantity}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-card-foreground">
                        ${item.price * item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          handleRemoveFromCart(item.month, item.gb)
                        }
                        className="text-xs bg-muted rounded px-2 py-1 text-muted-foreground hover:bg-muted/70"
                        type="button"
                        aria-label="Remove one"
                      >
                        −
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-muted">
              <span className="font-medium text-card-foreground">Total</span>
              <span className="font-bold text-lg text-primary">
                $<span>{total}</span>
              </span>
            </div>
            <div className="mt-8">
              <a
                href="https://app.paymento.io/payment-link/83441902c27446c2a6f49bfe7260137e"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex justify-center items-center bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base px-6 py-2 rounded transition-colors"
              >
                Checkout
              </a>
              <div className="flex items-center gap-2 mb-2 mt-6">
                <ScanIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  Pay with QR code
                </span>
              </div>
              <div className="flex justify-center">
                <img
                  src="/images/bd7558f1e851ab41fe47694e53128ef3.png"
                  alt="Payment QR Code"
                  className="w-32 h-32 rounded bg-muted"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
